import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import { useCampContext } from '../../context/CampContext';
import CampCard from '../camps/CampCard';
import CampDetails from '../camps/CampDetails';
import AddEventPopup from './AddEventPopup';
import EventEditPopup from './EventEditPopup';
import { Camp } from '../../utils/types';
import { parseLocalDate } from '../../utils/dateUtils';
import { getScheduledDateIdsForSchedule } from '../../utils/scheduleUtils';
import { useModalPosition } from '../../hooks/useModalPosition';
import { useAllCalendarEvents } from '../../hooks/useCalendarEvents';
import { X } from 'lucide-react';
import './Calendar.css';

interface CalendarProps {
  initialDate: Date | null;
  onShareClick?: () => void;
  onSummaryClick?: () => void;
}

const Calendar: React.FC<CalendarProps> = ({ initialDate, onShareClick, onSummaryClick }) => {
  const { schedule, schedules, sharedSchedules, selectedScheduleIds, currentScheduleUuid } = useCampContext();
  const calendarRef = useRef<FullCalendar>(null);
  const isInitialMount = useRef(true);
  const { calculatePosition, calculatePositionForElement } = useModalPosition();

  // Camp modal state
  const [selectedCamp, setSelectedCamp] = useState<Camp | null>(null);
  const [selectedCampDateId, setSelectedCampDateId] = useState<string | null>(null);
  const [selectedCampScheduleUuid, setSelectedCampScheduleUuid] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalPosition, setModalPosition] = useState<{ top: number; left?: number; right?: number; centered?: boolean }>({ top: 0 });
  const [showCampDetails, setShowCampDetails] = useState(false);

  // Date selection state
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [showDateSelectionPopup, setShowDateSelectionPopup] = useState(false);
  const [dateSelectionPopupPosition, setDateSelectionPopupPosition] = useState<{ top: number; left?: number; centered?: boolean }>({ top: 0, left: 0 });

  // Custom event edit state
  const [eventEditState, setEventEditState] = useState<{
    id: string | number | null;
    title: string;
    startDate: Date | null;
    endDate: Date | null;
    scheduleUuid: string | null;
    isOpen: boolean;
    position: { top: number; left?: number; centered?: boolean };
  }>({
    id: null,
    title: '',
    startDate: null,
    endDate: null,
    scheduleUuid: null,
    isOpen: false,
    position: { top: 0, left: 0 }
  });

  const allSchedules = useMemo(() => [...schedules, ...sharedSchedules], [schedules, sharedSchedules]);
  const events = useAllCalendarEvents(allSchedules, selectedScheduleIds);

  // Check if the current schedule is a shared schedule
  const isSharedSchedule = useMemo(() => {
    if (!currentScheduleUuid) return false;
    return sharedSchedules.some(s => s.uuid === currentScheduleUuid);
  }, [currentScheduleUuid, sharedSchedules]);

  const getScheduledDateIds = useCallback((campId: string, scheduleUuid?: string | null): string[] => {
    const scheduleToCheck = scheduleUuid
      ? allSchedules.find(s => s.uuid === scheduleUuid)
      : schedule;

    return getScheduledDateIdsForSchedule(campId, scheduleToCheck);
  }, [allSchedules, schedule]);

  // Handle custom event click
  const handleCustomEventClick = useCallback((info: any) => {
    const { eventId, eventData, scheduleUuid } = info.event.extendedProps;
    const mouseEvent = info.jsEvent as MouseEvent;

    // Close other popups
    setShowModal(false);
    setShowDateSelectionPopup(false);
    setShowCampDetails(false);

    const position = calculatePosition(mouseEvent, { width: 320, height: 250 });

    setEventEditState({
      id: eventId,
      title: eventData.title,
      startDate: parseLocalDate(eventData.start_date),
      endDate: parseLocalDate(eventData.end_date),
      scheduleUuid,
      isOpen: true,
      position
    });
  }, [calculatePosition]);

  // Handle camp event click
  const handleCampEventClick = useCallback((info: any) => {
    const { camp, campDateId, scheduleUuid } = info.event.extendedProps;

    // Close other popups
    setShowDateSelectionPopup(false);
    setEventEditState(prev => ({ ...prev, isOpen: false }));

    const position = calculatePositionForElement(info.el, { width: 500, height: 600 });

    setModalPosition(position);
    setSelectedCamp(camp);
    setSelectedCampDateId(campDateId);
    setSelectedCampScheduleUuid(scheduleUuid);
    setShowModal(true);
  }, [calculatePositionForElement]);

  // Handle event click (router)
  const handleEventClick = useCallback((info: any) => {
    const { type } = info.event.extendedProps;

    // Ignore holiday events - they are not editable
    if (type === 'holiday') return;

    if (type === 'custom') {
      handleCustomEventClick(info);
    } else if (type === 'camp') {
      handleCampEventClick(info);
    }
  }, [handleCustomEventClick, handleCampEventClick]);

  // Handle date selection (drag to select multiple dates)
  const handleDateSelect = useCallback((selectInfo: any) => {
    // Close other popups
    setShowModal(false);
    setEventEditState(prev => ({ ...prev, isOpen: false }));
    setShowCampDetails(false);

    const { start, end, jsEvent } = selectInfo;

    // Generate array of dates between start and end
    const dates: Date[] = [];
    const currentDate = new Date(start);
    const endDate = new Date(end);

    // Subtract one day from end because FullCalendar's end is exclusive
    endDate.setDate(endDate.getDate() - 1);

    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    setSelectedDates(dates);

    const mouseEvent = jsEvent as MouseEvent;
    const position = calculatePosition(mouseEvent, { width: 320, height: 200 });

    setDateSelectionPopupPosition(position);
    setShowDateSelectionPopup(true);
  }, [calculatePosition]);

  // Modal/popup close handlers
  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setSelectedCamp(null);
    setSelectedCampDateId(null);
    setSelectedCampScheduleUuid(null);
  }, []);

  const handleCloseDateSelectionPopup = useCallback(() => {
    setShowDateSelectionPopup(false);
    setSelectedDates([]);

    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.unselect();
    }
  }, []);

  const handleCloseEventEditPopup = useCallback(() => {
    setEventEditState({
      id: null,
      title: '',
      startDate: null,
      endDate: null,
      scheduleUuid: null,
      isOpen: false,
      position: { top: 0, left: 0 }
    });
  }, []);

  const handleCloseCampDetails = useCallback(() => {
    setShowCampDetails(false);
    setSelectedCamp(null);
    setSelectedCampDateId(null);
  }, []);

  // Other handlers
  const handleCampDateChange = useCallback((_campId: string, dateId: string) => {
    setSelectedCampDateId(dateId);
  }, []);

  const handleCampClick = useCallback((_camp: Camp) => {
    setShowModal(false);
    setShowCampDetails(true);
  }, []);

  // Close modal when clicking outside
  useEffect(() => {
    if (!showModal) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.calendar-modal-popup') && !target.closest('.fc-event')) {
        handleCloseModal();
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showModal, handleCloseModal]);

  // Navigate to appropriate date when calendar loads or schedule changes
  useEffect(() => {
    if (!calendarRef.current) return;

    const calendarApi = calendarRef.current.getApi();

    if (initialDate) {
      calendarApi.gotoDate(initialDate);
      isInitialMount.current = false;
    } else if (isInitialMount.current && schedule?.scheduled_camps && schedule.scheduled_camps.length > 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const futureCamps = schedule.scheduled_camps.filter(scheduled => {
        const startDate = parseLocalDate(scheduled.camp_date.start_date);
        return startDate >= today;
      });

      if (futureCamps.length > 0) {
        const earliestDate = futureCamps.reduce((earliest, scheduled) => {
          const startDate = parseLocalDate(scheduled.camp_date.start_date);
          return startDate < earliest ? startDate : earliest;
        }, parseLocalDate(futureCamps[0].camp_date.start_date));

        calendarApi.gotoDate(earliestDate);
      } else {
        calendarApi.gotoDate(today);
      }
      isInitialMount.current = false;
    }
  }, [schedule, initialDate]);

  // Toggle disabled state on share button
  useEffect(() => {
    if (!calendarRef.current) return;

    const shareButton = document.querySelector('.fc-shareButton-button');
    if (!shareButton) return;

    if (isSharedSchedule) {
      shareButton.classList.add('fc-state-disabled');
      shareButton.setAttribute('disabled', 'true');
    } else {
      shareButton.classList.remove('fc-state-disabled');
      shareButton.removeAttribute('disabled');
    }
  }, [isSharedSchedule]);

  return (
    <>
      <FullCalendar
        ref={calendarRef}
        plugins={[ dayGridPlugin, interactionPlugin ]}
        initialView="dayGridMonth"
        events={events}
        eventClick={handleEventClick}
        selectable={true}
        selectMirror={true}
        selectLongPressDelay={200}
        eventLongPressDelay={200}
        longPressDelay={200}
        select={handleDateSelect}
        unselectAuto={true}
        selectOverlap={true}
        customButtons={{
          ...(onShareClick ? {
            shareButton: {
              text: 'Share Calendar',
              click: () => {
                if (!isSharedSchedule && onShareClick) {
                  onShareClick();
                }
              }
            }
          } : {}),
          ...(onSummaryClick ? {
            summaryButton: {
              text: 'Summary',
              click: () => {
                if (onSummaryClick) {
                  onSummaryClick();
                }
              }
            }
          } : {})
        }}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: [onSummaryClick ? 'summaryButton' : '', onShareClick ? 'shareButton' : ''].filter(Boolean).join(',')
        }}
      />

      {showModal && selectedCamp && createPortal(
        <div
          className={`calendar-modal-popup ${modalPosition.centered ? 'calendar-modal-centered' : ''}`}
          style={{
            top: `${modalPosition.top}px`,
            left: modalPosition.left !== undefined ? `${modalPosition.left}px` : undefined,
            right: modalPosition.right !== undefined ? `${modalPosition.right}px` : undefined,
          }}
        >
          <button className="calendar-modal-close" onClick={handleCloseModal} aria-label="Close">
            <X className="icon icon--sm" />
          </button>
          <div className="calendar-modal-body">
            <CampCard
              camp={selectedCamp}
              selectedDate={selectedCampDateId || undefined}
              onDateChange={handleCampDateChange}
              favoriteId="1"
              scheduledDateIds={getScheduledDateIds(selectedCamp.id, selectedCampScheduleUuid)}
              scheduleUuidOverride={selectedCampScheduleUuid}
              onCampClick={handleCampClick}
            />
          </div>
        </div>,
        document.body
      )}

      {showCampDetails && selectedCamp && (
        <CampDetails
          camp={selectedCamp}
          isOpen={showCampDetails}
          onClose={handleCloseCampDetails}
          selectedDate={selectedCampDateId || undefined}
          favoriteId="1"
          scheduledDateIds={getScheduledDateIds(selectedCamp.id, selectedCampScheduleUuid)}
        />
      )}

      {showDateSelectionPopup && createPortal(
        <AddEventPopup
          selectedDates={selectedDates}
          onClose={handleCloseDateSelectionPopup}
          position={dateSelectionPopupPosition}
          scheduleUuid={schedule ? schedule.uuid : null}
        />,
        document.body
      )}

      {eventEditState.isOpen && eventEditState.id && eventEditState.startDate && eventEditState.endDate && eventEditState.scheduleUuid && createPortal(
        <EventEditPopup
          eventId={eventEditState.id}
          eventTitle={eventEditState.title}
          startDate={eventEditState.startDate}
          endDate={eventEditState.endDate}
          onClose={handleCloseEventEditPopup}
          position={eventEditState.position}
          scheduleUuid={eventEditState.scheduleUuid}
        />,
        document.body
      )}
    </>
  );
};

export default Calendar;
